import { useCallback } from "react"
import type { GuidebookHighlight, GuidebookPlace, GuidebookWeather } from "./types"

interface Props {
  highlights: GuidebookHighlight[]
  places: GuidebookPlace[]
  weather: GuidebookWeather | null
  onSelectPlace: (place: GuidebookPlace) => void
}

export function HighlightsStrip({ highlights, places, weather, onSelectPlace }: Props) {
  if (!highlights || highlights.length === 0) return null

  const handleClick = useCallback(
    (highlight: GuidebookHighlight) => {
      const place = places.find((p) => p.id === highlight.place_id)
      if (place) onSelectPlace(place)
    },
    [places, onSelectPlace],
  )

  // Resolve place name for each highlight
  const enriched = highlights.map((h) => {
    const place = places.find((p) => p.id === h.place_id)
    const copy = place?.en
    return { ...h, placeName: copy?.name || h.place_id }
  })

  // Time-specific header
  const timeLabel = getTimeLabel()

  // Weather context line
  const weatherContext = weather ? formatWeatherContext(weather) : null

  return (
    <section className="gb-highlights" aria-label="Concierge picks">
      <div className="gb-highlights__header">
        <div className="gb-highlights__header-row">
          <div className="gb-highlights__badge">Concierge</div>
          <h2 className="gb-highlights__title">{timeLabel}</h2>
        </div>
        {weatherContext && (
          <div className="gb-highlights__weather-context">{weatherContext}</div>
        )}
      </div>
      <div className="gb-highlights__scroll">
        {enriched.map((h) => (
          <button
            key={h.slot}
            type="button"
            className="gb-highlights__card"
            onClick={() => handleClick(h)}
          >
            <div className="gb-highlights__card-top">
              <span className="gb-highlights__emoji" aria-hidden="true">
                {h.emoji}
              </span>
              <span className="gb-highlights__label">{h.label}</span>
            </div>
            <p className="gb-highlights__motivation">{h.motivation}</p>
            <div className="gb-highlights__place-name">{h.placeName}</div>
          </button>
        ))}
      </div>
    </section>
  )
}

function getTimeLabel(): string {
  const hour = new Date().getHours()
  if (hour < 11) return "This morning's picks"
  if (hour < 18) return "This afternoon's picks"
  return "Tonight's picks"
}

function formatWeatherContext(w: GuidebookWeather): string | null {
  if (w.temperature_c == null) return null

  const temp = Math.round(w.temperature_c)
  const emoji = weatherEmoji(w.weather_code, w.is_day)
  const label = w.weather_label || ""

  let sunsetPart = ""
  if (w.sunset) {
    try {
      const d = new Date(w.sunset)
      const time = d.toLocaleTimeString("en-GB", {
        timeZone: w.timezone || undefined,
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      })
      sunsetPart = ` · Sunset ${time}`
    } catch { /* ignore */ }
  }

  return `${emoji} ${temp}° ${label}${sunsetPart}`
}

function weatherEmoji(code: number | null, isDay: boolean | null): string {
  if (code == null) return ""
  if (code === 0 || code === 1) return isDay ? "\u2600\uFE0F" : "\uD83C\uDF19"
  if (code === 2) return "\u26C5"
  if (code === 3) return "\u2601\uFE0F"
  if (code === 45 || code === 48) return "\uD83C\uDF2B\uFE0F"
  if (code >= 51 && code <= 57) return "\uD83C\uDF27\uFE0F"
  if (code >= 61 && code <= 67) return "\uD83C\uDF27\uFE0F"
  if (code >= 71 && code <= 77) return "\u2744\uFE0F"
  if (code >= 80 && code <= 82) return "\uD83C\uDF26\uFE0F"
  if (code >= 85 && code <= 86) return "\uD83C\uDF28\uFE0F"
  if (code >= 95) return "\u26C8\uFE0F"
  return "\uD83C\uDF24\uFE0F"
}
